import React from "react";

import {
  RadioGroup,
  Radio,
  RadioProps,
  RadioGroupProps,
  FormHelperText,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { colors } from "@zapalacorp/tokens";
import classnames from "classnames";

import styles, { Description, HeaderWrapper, RequiredLabel, Title } from "./styles";
import { styled } from "@mui/material/styles";

type AsideChild = {
  child: React.ReactNode;
  classnames: string;
};

export type OptionProps = {
  key?: string;
  value?: string | number | boolean | number[] | string[];
  label?: string;
  labelWithTooltip?: React.ReactChild;
  classes?: string;
  ["data-cy"]?: string;
  disabled?: boolean;
  asideChild?: AsideChild;
};

export type RadioButtonProps = RadioGroupProps & {
  options: OptionProps[];
  ["data-cy"]?: string;
  disabled?: boolean;
  isDirectionRow?: boolean;
  label?: string;
  error?: boolean;
  required?: boolean;
  errorMessage?: string;
  description?: string;
  checkMatcher?: (option: OptionProps, props: RadioGroupProps) => boolean;
};

interface RadioPersonalizedProps extends RadioProps {
  datacy?: string;
}

const StyledRadioButton = styled((props: RadioPersonalizedProps) => (
  <Radio data-cy={props.datacy?.toLowerCase().replace(/\s/g, "")} {...props} />
))(() => ({
  color: colors.gray500, // Cor padrão
  "&.Mui-checked": {
    color: `${colors.brandBlue500} !important`, // Cor quando selecionado
  },
  "&.Mui-disabled": {
    color: `${colors.gray400} !important`,
  },
}));

/**
 * @description Render a Radio group and your radio options.
 * @param options List of OptionProps to show raadio options inside RadioGroup.
 * @param value String value to fill a radio option.
 * @param onChange Callback function fired when select a radio option.
 * @param disabled Boolean value to use if radio has disabled
 * @param data-cy Unique string key to get on cypress
 * @param ...props All props of RadioGroup.
 * @returns A Radio button component with options sent via option params.
 */
export const RadioButton = ({
  options,
  label,
  error,
  errorMessage,
  disabled: radioDisabled,
  isDirectionRow = true,
  required = false,
  description,
  checkMatcher,
  ...props
}: RadioButtonProps) => {
  const classes = styles();

  const renderHeader = () => {
    if (label) {
      return (
        <HeaderWrapper>
          <Title disabled={radioDisabled}>
            {label}
            {required && <RequiredLabel>*</RequiredLabel>}
          </Title>
          {description && <Description>{description}</Description>}
        </HeaderWrapper>
      );
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
  };

  const internalCheckMatcher = (option: OptionProps, props: RadioGroupProps) => {
    if (checkMatcher) {
      return checkMatcher(option, props);
    }

    if (Array.isArray(option.value)) {
      return option.value.some((value) => value === props.value);
    }

    return option.value === props.value;
  };

  return (
    <FormControl>
      {renderHeader()}
      <RadioGroup row={isDirectionRow} {...props}>
        {options.map((option, index: number) => (
          <div key={option.key || index} className={option.asideChild?.classnames}>
            <FormControlLabel
              className={classnames(classes.radioLabel, option.classes)}
              value={option.value}
              control={
                <StyledRadioButton
                  checked={internalCheckMatcher(option, props)}
                  value={option.value}
                  datacy={option["data-cy"]}
                />
              }
              label={option.label || option.labelWithTooltip}
              disabled={radioDisabled || option.disabled}
            />
            {option.asideChild?.child}
          </div>
        ))}
      </RadioGroup>
      {renderError()}
    </FormControl>
  );
};

export default RadioButton;
